import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useQuery } from "@apollo/client/react";
import { ONSITE_MANIFEST_COLLECTION_QUERY } from "src/constants/graphql/queries";

const getFirstRackLocation = rackLocations => rackLocations?.[0];

const getMobileLocation = rackLocations => {
  const loc = getFirstRackLocation(rackLocations);
  return loc?.mobile_warehouse_location?.location_name || loc?.mobileWarehouseLocation?.locationName || "—";
};

const getWarehouseLocation = rackLocations => {
  const loc = getFirstRackLocation(rackLocations);
  const wl = loc?.warehouse_locations?.[0] || loc?.warehouseLocations?.[0];
  return wl?.location_name || wl?.locationName || "—";
};

const COLUMNS = [
  { id: "status", label: "Status" },
  { id: "description", label: "Name" },
  { id: "manufacturer", label: "Manufacturer" },
  { id: "model", label: "Model" },
  { id: "serialNumber", label: "Serial #" },
  { id: "mobileLocation", label: "Rack ID" },
  { id: "warehouseLocation", label: "Warehouse Location" },
  { id: "quantity", label: "Qty" },
];

const getSortValue = (sku, field) =>
  field === "mobileLocation" ? getMobileLocation(sku.rackLocations) : (sku[field] || "");

export default function ManifestOverviewTab({ project }) {
  const [sortField, setSortField] = useState("mobileLocation");
  const [sortDir, setSortDir] = useState("asc");

  const { data, loading } = useQuery(ONSITE_MANIFEST_COLLECTION_QUERY, {
    variables: { projectId: project.id },
  });

  const skus = data?.assetsCollection?.collection || [];

  const sorted = useMemo(() => {
    return [...skus].sort((a, b) => {
      const cmp = getSortValue(a, sortField).localeCompare(getSortValue(b, sortField));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [skus, sortField, sortDir]);

  const handleSort = field => {
    setSortDir(sortField === field && sortDir === "asc" ? "desc" : "asc");
    setSortField(field);
  };

  const validatedCount = skus.filter(s => s.validationListValidated).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" gap={2} mb={2} alignItems="center">
        <Chip
          icon={<CheckCircleIcon fontSize="small" />}
          label={`${validatedCount} Validated`}
          color="success"
          size="small"
          variant="outlined"
        />
        <Chip
          icon={<RadioButtonUncheckedIcon fontSize="small" />}
          label={`${skus.length - validatedCount} Pending`}
          color="warning"
          size="small"
          variant="outlined"
        />
      </Box>
      {skus.length === 0 ? (
        <Box py={4} textAlign="center">
          <Typography color="textSecondary">No manifest items found for this project.</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 420, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell key={col.id} sx={{ bgcolor: "background.paper", fontWeight: 600, whiteSpace: "nowrap" }}>
                    {col.id === "status" ? (
                      col.label
                    ) : (
                      <TableSortLabel
                        active={sortField === col.id}
                        direction={sortField === col.id ? sortDir : "asc"}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map(sku => {
                const validated = Boolean(sku.validationListValidated);
                return (
                  <TableRow key={sku.id} sx={{ opacity: validated ? 0.5 : 1 }}>
                    <TableCell>
                      {validated ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : (
                        <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                      )}
                    </TableCell>
                    <TableCell>{sku.description || "—"}</TableCell>
                    <TableCell>{sku.manufacturer || "—"}</TableCell>
                    <TableCell>{sku.model || "—"}</TableCell>
                    <TableCell>{sku.serialNumber || "—"}</TableCell>
                    <TableCell>{getMobileLocation(sku.rackLocations)}</TableCell>
                    <TableCell>{getWarehouseLocation(sku.rackLocations)}</TableCell>
                    <TableCell>{sku.quantity ?? "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
